import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";

import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import type { GridColDef } from "@mui/x-data-grid";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDDialog from "components/MDDialog";
import { LojaDataGrid } from "layouts/pages/loja/common";

import FormCupomEvento from "./components/FormCupomEvento";
import {
  CUPOM_ACCENT,
  cupomModalHeaderSx,
  cupomModalPaperSx,
  cupomToolbarSx,
} from "./cupom-evento.theme";

import { ICupomEvento } from "interfaces/cupom";
import { IEventoResponse } from "interfaces/eventos";
import { formatCurrency } from "helpers/utils";
import { deleteCupom, getAllCupomEvento } from "services/cupons";

interface Props {
  evento: IEventoResponse;
}

function formatarData(valor?: string | null): string {
  if (!valor) return "—";
  return format(
    valor.slice(0, 10).concat("T23:59:59.000Z"),
    "dd/MM/yyyy",
  );
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        color={accent ? CUPOM_ACCENT : "text.primary"}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function CupomEvento({ evento }: Props) {
  const [formOpen, setFormOpen] = useState(false);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [listCupom, setListCupom] = useState<ICupomEvento[]>([]);
  const [selectedCupom, setSelectedCupom] = useState<ICupomEvento | null>(null);
  const [loading, setLoading] = useState(true);

  const loadCupons = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getAllCupomEvento(evento.id);
      setListCupom(resp);
    } finally {
      setLoading(false);
    }
  }, [evento.id]);

  useEffect(() => {
    void loadCupons();
  }, [loadCupons]);

  const stats = useMemo(() => {
    const disponiveis = listCupom.reduce(
      (sum, item) => sum + item.disponivel,
      0,
    );
    const utilizados = listCupom.reduce(
      (sum, item) => sum + (item.qtdCupom - item.disponivel),
      0,
    );
    return { total: listCupom.length, disponiveis, utilizados };
  }, [listCupom]);

  const handleSelectedCupom = (cupom: ICupomEvento) => {
    setSelectedCupom(cupom);
    setOpenDialogDelete(true);
  };

  const handleDeleteCupom = async () => {
    if (!selectedCupom?.id) return;
    await deleteCupom(selectedCupom.id);
    setSelectedCupom(null);
    setOpenDialogDelete(false);
    await loadCupons();
  };

  const handleUpdateListCupom = (
    cupom: ICupomEvento,
    action: "create" | "edit",
  ) => {
    if (action === "create") {
      setListCupom((prev) => [...prev, cupom]);
    } else {
      setListCupom((prev) => [
        ...prev.filter((item) => item.id !== cupom.id),
        cupom,
      ]);
    }
    setFormOpen(false);
    setSelectedCupom(null);
  };

  const abrirNovo = () => {
    setSelectedCupom(null);
    setFormOpen(true);
  };

  const handleEditCupom = (cupom: ICupomEvento) => {
    setSelectedCupom(cupom);
    setFormOpen(true);
  };

  const fecharForm = () => {
    setFormOpen(false);
    setSelectedCupom(null);
  };

  const columns: GridColDef<ICupomEvento>[] = useMemo(
    () => [
      {
        field: "codigoPromocional",
        headerName: "Código",
        flex: 1,
        minWidth: 140,
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={700} noWrap>
            {params.row.codigoPromocional.toUpperCase()}
          </Typography>
        ),
      },
      {
        field: "dataInicio",
        headerName: "Início",
        width: 110,
        valueFormatter: (params) =>
          formatarData(params.value as string | undefined),
      },
      {
        field: "validade",
        headerName: "Validade",
        width: 110,
        valueFormatter: (params) =>
          formatarData(params.value as string | undefined),
      },
      {
        field: "qtdCupom",
        headerName: "Utilizados",
        width: 110,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Typography variant="body2">
            {params.row.qtdCupom - params.row.disponivel} /{" "}
            {params.row.qtdCupom}
          </Typography>
        ),
      },
      {
        field: "disponivel",
        headerName: "Disponível",
        width: 100,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={700}>
            {params.row.disponivel}
          </Typography>
        ),
      },
      {
        field: "valorCupom",
        headerName: "Desconto",
        width: 100,
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={600}>
            {params.row.tipoPorcentagem
              ? `${params.row.valorCupom}%`
              : formatCurrency(Number(params.row.valorCupom) || 0)}
          </Typography>
        ),
      },
      {
        field: "tipoPorcentagem",
        headerName: "Tipo",
        width: 80,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.row.tipoPorcentagem ? "%" : "R$"}
            variant="outlined"
          />
        ),
      },
      {
        field: "colorField",
        headerName: "Cor",
        width: 60,
        sortable: false,
        renderCell: (params) => (
          <Box
            sx={{
              width: 22,
              height: 22,
              backgroundColor: params.row.colorField,
              borderRadius: "50%",
              border: "1px solid",
              borderColor: "grey.300",
            }}
          />
        ),
      },
      {
        field: "acoes",
        headerName: "",
        width: 88,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.25}>
            <Tooltip title="Editar">
              <IconButton
                size="small"
                color="info"
                onClick={() => handleEditCupom(params.row)}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleSelectedCupom(params.row)}
              >
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [],
  );

  return (
    <MDBox>
      <MDDialog
        title="Excluir cupom"
        message={`Deseja excluir o cupom "${selectedCupom?.codigoPromocional.toUpperCase()}"?`}
        open={openDialogDelete}
        onClose={() => {
          setOpenDialogDelete(false);
          setSelectedCupom(null);
        }}
        response={(resp: boolean) => {
          if (resp) void handleDeleteCupom();
          else setOpenDialogDelete(false);
        }}
      />

      <Box sx={cupomToolbarSx}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ConfirmationNumberOutlinedIcon sx={{ color: CUPOM_ACCENT }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Cupons de desconto
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {evento.titulo}
              </Typography>
            </Box>
          </Stack>
          <MDButton
            variant="gradient"
            color="dark"
            onClick={abrirNovo}
            startIcon={<AddOutlinedIcon />}
            sx={{
              flexShrink: 0,
              background: `linear-gradient(195deg, ${CUPOM_ACCENT}, #e04a1a)`,
            }}
          >
            Novo cupom
          </MDButton>
        </Stack>
      </Box>

      {loading && listCupom.length === 0 ? (
        <Grid container spacing={2} mb={2.5}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={6} sm={4} key={i}>
              <Skeleton variant="rounded" height={88} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2} mb={2.5}>
          <Grid item xs={6} sm={4}>
            <StatCard label="Total de cupons" value={stats.total} accent />
          </Grid>
          <Grid item xs={6} sm={4}>
            <StatCard label="Disponíveis" value={stats.disponiveis} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard label="Utilizados" value={stats.utilizados} />
          </Grid>
        </Grid>
      )}

      {loading ? (
        <Box sx={{ py: 2 }}>
          <Skeleton variant="rounded" height={320} />
        </Box>
      ) : listCupom.length === 0 ? (
        <Box
          sx={{
            py: 6,
            px: 2,
            textAlign: "center",
            borderRadius: 2,
            border: "1px dashed",
            borderColor: "grey.300",
            bgcolor: "grey.50",
          }}
        >
          <ConfirmationNumberOutlinedIcon
            sx={{ fontSize: 44, color: "grey.400", mb: 1 }}
          />
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Nenhum cupom cadastrado
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Crie cupons promocionais para incentivar inscrições neste evento.
          </Typography>
          <MDButton
            variant="outlined"
            color="dark"
            onClick={abrirNovo}
            startIcon={<AddOutlinedIcon />}
          >
            Adicionar primeiro cupom
          </MDButton>
        </Box>
      ) : (
        <LojaDataGrid
          rows={listCupom}
          columns={columns}
          getRowId={(row) => row.id}
          autoHeight
          disableColumnMenu
          disableRowSelectionOnClick
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: { sortModel: [{ field: "codigoPromocional", sort: "asc" }] },
          }}
          pageSizeOptions={[5, 10, 25]}
          localeText={{ noRowsLabel: "Nenhum cupom cadastrado" }}
          sx={{
            "& .MuiDataGrid-row:hover": {
              bgcolor: "rgba(249, 96, 48, 0.04)",
            },
          }}
        />
      )}

      <Dialog
        open={formOpen}
        onClose={fecharForm}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: cupomModalPaperSx }}
      >
        <Box sx={cupomModalHeaderSx}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              component="img"
              src={evento.urlImageDestaque}
              alt={evento.titulo}
              sx={{
                width: 56,
                height: 56,
                borderRadius: 1.5,
                objectFit: "cover",
                border: "2px solid rgba(255,255,255,0.2)",
                flexShrink: 0,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="overline"
                sx={{ opacity: 0.75, letterSpacing: 1 }}
              >
                {selectedCupom ? "Editar cupom" : "Novo cupom"}
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {evento.titulo}
              </Typography>
            </Box>
            <IconButton
              onClick={fecharForm}
              sx={{ color: "inherit" }}
              aria-label="Fechar"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        <DialogContent sx={{ p: { xs: 2, sm: 2.5 }, overflow: "auto" }}>
          <FormCupomEvento
            evento={evento}
            cupom={selectedCupom}
            updateListCupom={handleUpdateListCupom}
          />
        </DialogContent>
      </Dialog>
    </MDBox>
  );
}
